import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Star, Quote } from 'lucide-react';
import { GoogleReviewButton } from './GoogleReviewButton';

export const ReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ total_reviews: 0, average_rating: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reviewsRes, statsRes] = await Promise.all([
        axios.get(`${API}/reviews?limit=6`),
        axios.get(`${API}/reviews/stats`)
      ]);
      setReviews(reviewsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch reviews', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
      />
    ));
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  if (loading || reviews.length === 0) return null;

  return (
    <section className="py-24 px-6 bg-background" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-sm uppercase tracking-[0.2em] mb-3 font-mono text-accent">Customer Reviews</p>
          <h2 className="text-4xl sm:text-5xl font-bold mb-6" style={{ fontFamily: 'Cabinet Grotesk' }} data-testid="reviews-title">
            What Our Customers Say
          </h2>
          
          <div className="inline-flex items-center gap-4 bg-white rounded-full px-8 py-4 shadow-sm border border-border">
            <div className="flex items-center gap-1">
              {renderStars(Math.round(stats.average_rating))}
            </div>
            <div className="text-left">
              <p className="text-2xl font-bold" data-testid="reviews-avg-rating">{stats.average_rating}</p>
              <p className="text-xs text-muted-foreground" data-testid="reviews-total-count">Based on {stats.total_reviews} reviews</p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map((review, index) => (
            <div
              key={review.id}
              className="bg-card p-6 rounded-lg border border-border relative hover:shadow-lg transition-shadow"
              data-testid={`review-card-${index}`}
            >
              <Quote className="absolute top-4 right-4 w-8 h-8 text-accent/20" />
              
              <div className="flex items-center gap-1 mb-4">
                {renderStars(review.rating)}
              </div>

              <p className="text-foreground mb-6 relative z-10" data-testid={`review-comment-${index}`}>
                "{review.comment}"
              </p>

              <div className="flex items-center gap-3 pt-4 border-t border-border">
                <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-bold text-sm">
                  {getInitials(review.user_name)}
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-sm" data-testid={`review-name-${index}`}>{review.user_name}</p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{formatDate(review.created_at)}</span>
                    {review.car_name && (
                      <>
                        <span>•</span>
                        <span className="text-accent" data-testid={`review-car-${index}`}>{review.car_name}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Google Review CTA */}
        <div className="mt-12 text-center">
          <p className="text-lg text-muted-foreground mb-4">Had a great experience with us?</p>
          <GoogleReviewButton />
        </div>
      </div>
    </section>
  );
};
